// FEAT-026 — config dual-mode: el launcher puede apuntar al backend local o a los 4 slots de prod.
//
// Cómo se elige el modo:
//   1) Override por URL: ?mode=local | ?mode=prod  (persiste en sessionStorage)
//   2) Persistido en sessionStorage
//   3) Default: 'local' si hostname es localhost / 127.0.0.1, sino 'prod'

// Backend C# en local (donde corre Verify / ConsumeHandoff / getCentros).
const LOCAL_BACKEND_ORIGIN = 'http://localhost:44300';

// Frontend AngularJS legacy en local (donde vive el state loginHandoff).
// Vive en un puerto distinto al backend porque está servido standalone (no por IIS Express).
const LOCAL_LEGACY_FRONTEND = 'http://127.0.0.1:8083/index_deb.html';

const PROD_SLOT_HOSTS = [
  'tomoturnos.com',
  'tomo-turnos.com',
  'bookandcome.com',
  'tomoturnos.club'
];

function isLocalHost() {
  return location.hostname === 'localhost' || location.hostname === '127.0.0.1';
}

function detectMode() {
  const params = new URLSearchParams(location.search);
  const override = params.get('mode');
  if (override === 'local' || override === 'prod') {
    sessionStorage.setItem('launcher.mode', override);
    return override;
  }
  const stored = sessionStorage.getItem('launcher.mode');
  if (stored === 'local' || stored === 'prod') return stored;
  return isLocalHost() ? 'local' : 'prod';
}

// PICK_MODE: qué hacer al elegir un centro.
//   'inline'   → abrir form de login en el launcher (FEAT-026, dev por default).
//   'redirect' → ir directo al sitio del centro (default en prod, único modo permitido fuera de localhost).
function detectPickMode() {
  const params = new URLSearchParams(location.search);
  const override = params.get('pickMode');
  if (override === 'inline' || override === 'redirect') {
    if (isLocalHost()) sessionStorage.setItem('launcher.pickMode', override);
    return isLocalHost() ? override : 'redirect';
  }
  if (!isLocalHost()) return 'redirect';
  const stored = sessionStorage.getItem('launcher.pickMode');
  if (stored === 'inline' || stored === 'redirect') return stored;
  return 'inline';
}

export const MODE = detectMode();
export const PICK_MODE = detectPickMode();
export const IS_DEV_HOST = isLocalHost();

export const SLOT_URLS = MODE === 'local'
  ? [`${LOCAL_BACKEND_ORIGIN}/api/login3/getCentros`]
  : PROD_SLOT_HOSTS.map(h => `https://${h}/api/login3/getCentros`);

// Para llamadas al backend de un centro específico (verify, consumeHandoff).
// En local todos los centros viven en el mismo backend C# local.
// En prod el backend del centro es `centro.url`.
// ── Host confiable de un centro (16/9/2026) ────────────────────────────────────
// `centro.url` lo manda el backend desde Configuracion.Dominio, que sale del
// config.json de CADA slot en el servidor — no viaja en la DLL ni está bajo git.
// Un valor mal cargado ahí mandaba a TODOS los centros de ese slot a un host
// inexistente: el 16/9/2026 el slot 1 devolvía `landing.tomoturnos.com` y elegir
// un centro rebotaba a la landing en vez de entrar al sistema.
// Defensa: si `centro.url` no es uno de los 4 hosts conocidos, se usa `slotHost`
// — el host por el que ese centro efectivamente respondió, que lo sella
// api/slots.js y no puede estar mal porque es con el que se hizo el fetch.
export function hostForCentro(centro) {
  if (!centro) return null;
  if (PROD_SLOT_HOSTS.includes(centro.url)) return centro.url;
  if (PROD_SLOT_HOSTS.includes(centro.slotHost)) {
    if (centro.url) {
      console.warn(
        `[launcher] centro "${centro.alias}": url="${centro.url}" no es un slot conocido; ` +
        `se usa slotHost="${centro.slotHost}". Revisar "dominio" en el config.json de ese slot.`
      );
    }
    return centro.slotHost;
  }
  // Ninguno de los dos sirve: devolvemos lo que haya para no romper el flujo con
  // un null (el redirect fallará, pero de forma visible y con el warning arriba).
  return centro.url || centro.slotHost || null;
}

export function backendOriginForCentro(centro) {
  if (MODE === 'local') return LOCAL_BACKEND_ORIGIN;
  return `https://${hostForCentro(centro)}`;
}

// URL base del HTML del frontend legacy AngularJS (donde se montan los hash routes).
// Los redirects al legacy se construyen como `${legacyBaseUrl(c)}#/loginHandoff/<alias>?...`.
// En local: http://127.0.0.1:8083/index_deb.html#/loginHandoff/...
// En prod:  //<centro.url>/app/#/loginHandoff/...
// OPS-055 Fase B — LA PERILLA del rollout de la v2: los slots cuyo host esté acá reciben
// `/v2/` (Vue); el resto sigue en `/app/` (AngularJS). Es per-slot por diseño, porque el
// dominio ya determina el slot. Agregar hosts de a UNO y esperando entre cada uno (Fase C:
// s4 → s2/s3 → s1, el slot 1 siempre último).
//
// ROLLBACK = sacar el host de este array y redeployar el launcher (~1 min). Afecta el
// PRÓXIMO ingreso, no las sesiones vivas: quien ya está adentro de la v2 termina lo que
// está haciendo. **Esta sigue siendo la ÚNICA perilla de rollback.**
//
// ⚠️ Acá decía "NO desactivar la route del Worker como primera medida — sin route, /v2/*
// cae a Arvixe (que no tiene esa carpeta) y quien recargue se come un 404 duro". Eso
// quedó VIEJO el 23/9/2026: OPS-059 eliminó el Worker del camino y ahora `/v2/` lo sirve
// IIS desde el slot (index.html + sw + manifest + favicon + web.config, subidos por
// `deploy_estaticos_slots.ps1 -Que v2index,v2sw,v2fcmsw,v2manifest,v2favicon,v2config`).
// O sea: ya no hay route que desactivar, y la carpeta /v2 SÍ existe en el slot.
// La precondición para sumar un host acá es que esos 6 archivos estén subidos a ESE slot.
// Historial de esta perilla (cuatro ciclos): abierta 26/8/2026 17:02 → rollback 18:07 (freeze
// del browser por un loop reactivo; ver docs/tablero/tareas/v2-freeze-loop-reactivo-getservicio.md)
// → reabierta 27/8 07:10 con los dos bloqueantes corregidos → rollback 27/8 11:40 (pedido de
// Martín: quedaban cosas por verificar) → REABIERTA 28/8/2026 10:35 (pedido de Martín).
// Para cerrarla de nuevo: dejar el array vacío y redeployar el launcher.
// 23/9/2026 — se suman slot 2 y slot 3 (~3.000 logins/dia cada uno). Decision de Martin:
// acelerar la evidencia antes del viaje del 2-19/10 pasando de 200 a ~6.200 ingresos
// diarios sobre la v2, y dejar el SLOT 1 (~10.000 logins/dia, centros grandes con las
// configuraciones mas viejas) para DESPUES de las vacaciones. Precondiciones cumplidas:
// OPS-059 corrio primero (el index nace en el slot, el Worker ya no interviene) y los 6
// archivos estan subidos y verificados en los dos slots.
// 23/9/2026 (tarde) — se suma el SLOT 1, el ultimo que faltaba: **los 4 slots en la v2**.
// Martin pidio los datos antes de decidir y desmintieron la objecion de "el slot 1 es
// especial": tiene 208 centros contra los 328 de los otros tres juntos, y los cuatro
// arrancan en idEmpresa=1, o sea que los centros viejos estan repartidos, no concentrados
// ahi. La diferencia era de volumen de usuarios, no de naturaleza.
// Precondiciones cumplidas: los 6 archivos subidos y verificados en el slot 1, y el drift
// de IIS que impedia el `no-cache` en los .html resuelto ese mismo dia (Arvixe apago SSI;
// ver docs/tablero/tareas/slot1-drift-cache-html.md).
// ⚠️ Con esto AngularJS deja de servirse por el camino normal en los 4 slots: es lo que
// elimina ALTA-6 de SEC-035 de raiz, en vez de taparla con un WAF (ver SEC-044 §2.1).
// 23/9/2026 16:19 — se sumo el slot 1 y hubo que revertir a los 2 minutos: su index
// pedia `index-Dd3l4UzA.js`, un bundle que NUNCA se habia publicado al CDN, y Pages
// devolvia su fallback HTML => "Failed to load module script ... MIME type text/html".
// Causa: el index se subio a mano con deploy_estaticos_slots.ps1 despues de un
// `npm run build` que cambio los hashes, sin correr start-v2.bat (que publica los assets
// PRIMERO). El md5 del index coincidia con el local y el sitio estaba roto igual: lo que
// hay que verificar no es el index sino que SUS assets existan en el CDN.
// 23/9/2026 16:30 — reabierto tras correr el deploy completo. Verificado antes de subir:
// los 4 slots piden el mismo bundle y el CDN lo sirve como application/javascript.
// 24/9/2026 14:41 — ROLLBACK del slot 1 a la v1, pedido de Martín: *"varios clientes
// encontraron problemas y me daña la reputación"*. Los slots 2/3/4 siguen en la v2.
// Para volver a abrirlo: sumar 'tomoturnos.com' acá + build + start-go.bat (los archivos
// de /v2/ siguen subidos y al día en el slot 1, así que no hace falta nada más).
// 24/9/2026 16:30 — slot 1 REABIERTO a la v2, pedido de Martín. La causa del rollback
// de las 14:41 (buscadores que fallaban en Android con teclado predictivo) se arregló en
// a9491dc1 + 5b0a7d54, con un gate en el build (scripts/check-buscadores.mjs) para que
// no vuelva. Ver docs/tablero/tareas/v2-buscadores-android-teclado-predictivo.md.
const V2_HOSTS = ['tomoturnos.club', 'tomo-turnos.com', 'bookandcome.com', 'tomoturnos.com'];

export function legacyBaseUrl(centro) {
  if (MODE === 'local') return LOCAL_LEGACY_FRONTEND;
  // hostForCentro, no centro.url: ver el comentario de arriba (config.json de un
  // slot con el dominio mal mandaba el redirect a un host inexistente).
  const host = hostForCentro(centro);
  if (V2_HOSTS.includes(host)) return `//${host}/v2/`;
  return `//${host}/app/`;
}

// Lista de slot URLs (sin protocolo) — el launcher consulta el API de Launcher distribuido
// en TODOS los slots en paralelo. En modo local hay un único origin.
export const LAUNCHER_SLOT_URLS = MODE === 'local'
  ? [LOCAL_BACKEND_ORIGIN]
  : PROD_SLOT_HOSTS.map(h => `https://${h}`);

// Origin a usar para una URL/hostname de slot dado (`centro.url` o el `slotUrl` que viene
// en la respuesta de MyFolders). En local mapea todo al backend C# de localhost.
export function originForSlot(slotUrlOrHost) {
  if (MODE === 'local') return LOCAL_BACKEND_ORIGIN;
  if (!slotUrlOrHost) return null;
  // Tolera que venga con o sin protocolo. Normalizamos a https://hostname.
  if (slotUrlOrHost.startsWith('http://') || slotUrlOrHost.startsWith('https://')) return slotUrlOrHost;
  return `https://${slotUrlOrHost}`;
}

export function setMode(newMode) {
  if (newMode !== 'local' && newMode !== 'prod') return;
  sessionStorage.setItem('launcher.mode', newMode);
  location.reload();
}

export function setPickMode(newPickMode) {
  if (newPickMode !== 'inline' && newPickMode !== 'redirect') return;
  if (!isLocalHost()) return; // fuera de localhost no se puede cambiar
  sessionStorage.setItem('launcher.pickMode', newPickMode);
  location.reload();
}

// ─── Blog (Substack) ────────────────────────────────────────────────────────
// El Worker `tomoturnos-blog` sirve el feed ya parseado y las portadas redimensionadas
// bajo nuestro propio origen. Ver C:\projects\CloudFlare\blog-worker\README.md.
//
// En localhost pegamos contra el Worker de prod (tiene CORS abierto) para poder diseñar
// la card sin levantar `wrangler dev`; con `?blogBase=http://localhost:8787/blog` se
// apunta al Worker local. El override es SOLO dev a propósito: en producción un query
// param no puede decidir de dónde sale contenido que después renderizamos.
export const BLOG_BASE = (() => {
  if (!isLocalHost()) return '/blog';
  const override = new URLSearchParams(location.search).get('blogBase');
  return override ? override.replace(/\/+$/, '') : 'https://go.tomoturnos.com/blog';
})();

// Cuántas veces se muestra UNA nota antes de pasar a la siguiente. NO es un interruptor
// de apagado: agotado el número, la card no desaparece — rota a la nota que sigue. La
// idea es evitar el empapelado (la misma foto 30 veces se vuelve invisible) sin perder
// la superficie, que es justamente lo que fuimos a buscar acá.
//
// Con 6 notas en el feed son ~18 cargas antes de que el ciclo vuelva a empezar.
export const BLOG_VISTAS_POR_NOTA = 3;

export const CACHE_KEY_CENTROS = 'centros';
export const CACHE_VERSION = 1;

// TTL "soft": después de N días la cache se refresca de oficio en el próximo bootstrap.
// Calibrado a ~30 días porque solo se agregan ~10 centros nuevos/mes — refrescar más
// seguido sería desperdicio. El refresh on-demand (botón en "sin resultados") cubre el
// caso edge de un user buscando un centro recién agregado este mes.
export const CENTROS_SOFT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

// Anti-abuso del botón refresh manual: máximo 1 refresh por día por device.
export const MANUAL_REFRESH_COOLDOWN_MS = 24 * 60 * 60 * 1000;

// Cap visible de "Tus centros recientes": se muestran los 5 de acceso más reciente y los
// más viejos van saliendo de la lista (decisión de Martín 7/8/26 — antes eran 4).
// Esto capa solo el render, pero ya no es lo único que acota: desde el 18/9/26 el cupo real
// lo pone el backend (MAX_CENTROS_POR_DEVICE en LauncherController, LRU por slot) y el sync
// replica esa poda en los knownLogins locales. Antes acá decía que el storage no se podaba
// nunca, y era cierto: crecía sin techo y la lista se llenaba de centros fósiles.
export const MAX_CENTROS_RECIENTES = 5;

export const LAUNCHER_VERSION = '0.2.0';
